/**
 * Referral source tracking — captures ?ref= query param and stores it
 * in localStorage so we can attribute signups + events back to the
 * traffic source that brought the visitor (Reddit, Twitter, etc.).
 *
 * Usage:
 *   - <RefCaptureMount /> rendered once near the App root
 *   - getReferralSource() to read the stored value from anywhere
 */
const STORAGE_KEY = 'referral_source';
const STORAGE_TS_KEY = 'referral_source_ts';
const TTL_DAYS = 30;

export function captureReferralFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    // Accept either ?ref= or any utm_source= as a fallback
    const ref = (params.get('ref') || params.get('utm_source') || '').trim().toLowerCase();
    if (!ref) return getReferralSource();
    // Sanitize — keep it short and alnum-ish
    const clean = ref.replace(/[^a-z0-9_-]/gi, '').slice(0, 32);
    if (!clean) return getReferralSource();
    localStorage.setItem(STORAGE_KEY, clean);
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    return clean;
  } catch (_e) {
    return null;
  }
}

export function getReferralSource() {
  if (typeof window === 'undefined') return null;
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || '0', 10);
    if (ts && Date.now() - ts > TTL_DAYS * 24 * 60 * 60 * 1000) {
      // expired
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TS_KEY);
      return null;
    }
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch (_e) {
    return null;
  }
}

export function clearReferralSource() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TS_KEY);
  } catch (_e) {}
}

/**
 * POST a small analytics event. Always non-blocking, never throws.
 */
export async function logAnalyticsEvent(eventName, properties = {}) {
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return;
    const ref = getReferralSource();
    await fetch(`${apiUrl}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        event_name: String(eventName).slice(0, 64),
        referral_source: ref,
        properties,
        page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      }),
    });
  } catch (_e) {
    /* swallow — analytics is best-effort */
  }
}
